import { getCurrentInstance } from 'vue';
import { AIFeed } from '~/api/ai-feed';
import { UIAIUserService } from './ai-im/services/UIUserServies';
import { AIGenderType } from '~/store/modules/ai-feed';

const dbName = 'BotDatabase';
const dbVersion = 1;
const likesStoreName = 'likes';
const usersStoreName = 'users';

let db: IDBDatabase;

export const AIChatLocalizeService = () => {
    const { proxy } = getCurrentInstance() as { proxy: Record<string, any> };
    const openDatabase = (): Promise<IDBDatabase> => {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(dbName, dbVersion);

            request.onupgradeneeded = event => {
                db = (event.target as IDBRequest).result;

                // 创建 likes 对象存储空间
                const likesStore = db.createObjectStore(likesStoreName, { keyPath: 'id' });
                likesStore.createIndex('userId', 'userId', { unique: false });

                // 创建 users 对象存储空间
                const usersStore = db.createObjectStore(usersStoreName, { keyPath: 'id' });
                usersStore.createIndex('userId', 'userId', { unique: true });
            };

            request.onsuccess = event => {
                db = (event.target as IDBRequest).result;
                resolve(db);
            };

            request.onerror = event => {
                const { error } = event.target as IDBRequest;
                reject(error);
            };
        });
    };

    const addLike = async (bot: AIFeed): Promise<void> => {
        const userId = UIAIUserService.getUserIdByUserName(`${bot?.bot_id_str}-${bot?.bot_name}`);

        let user = UIAIUserService.getUserById(userId);
        if (!user) {
            user = await UIAIUserService.addUser<AIFeed>(`${bot?.bot_id_str}-${bot?.bot_name}`, true, bot);
        }
        await UIAIUserService.updateUser(userId, user.name, true);
        proxy?.$store?.commit('conversationModule/updateLikeUsers', { botId: bot?.bot_id_str });
    };

    const addUnlike = async (bot: AIFeed): Promise<void> => {
        const userId = UIAIUserService.getUserIdByUserName(`${bot?.bot_id_str}-${bot?.bot_name}`);
        const user = UIAIUserService.getUserById(userId);
        if (user?.like === true) {
            await UIAIUserService.updateUser(userId, user.name, false);
        }
        proxy?.$store?.commit('conversationModule/updateLikeUsers', { botId: bot?.bot_id_str });
    };

    const isBotLikedByUser = (bot: AIFeed) => {
        const userId = UIAIUserService.getUserIdByUserName(`${bot?.bot_id_str}-${bot?.bot_name}`);
        const user = UIAIUserService.getUserById(userId);
        if (user) {
            return user.like;
        }
        return false;
    };

    const getLikesByUser = async (userId: string): Promise<Array<{ id: string; userId: string; botId: string; timestamp: string }>> => {
        await openDatabase();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([likesStoreName], 'readonly');
            const store = transaction.objectStore(likesStoreName);
            const index = store.index('userId');

            const request = index.getAll(IDBKeyRange.only(userId));

            request.onsuccess = () => {
                resolve(request.result);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    };

    const getUser = (bot: AIFeed) => {
        const userId = UIAIUserService.getUserIdByUserName(`${bot?.bot_id_str}-${bot?.bot_name}`);
        return UIAIUserService.getUserById(userId);
    };
    const addUser = async (userId: string, isshowOver18: boolean): Promise<void> => {
        await openDatabase();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([usersStoreName], 'readwrite');
            const store = transaction.objectStore(usersStoreName);

            // 检查用户是否存在
            const getRequest = store.get(userId);

            getRequest.onsuccess = event => {
                const existingUser = getRequest.result;

                if (existingUser) {
                    // 用户已存在，更新信息
                    const updateRequest = store.put({ ...existingUser, isshowOver18 });

                    updateRequest.onsuccess = () => {
                        resolve();
                    };

                    updateRequest.onerror = () => {
                        reject(updateRequest.error);
                    };
                } else {
                    // 用户不存在，添加新记录
                    const newUser = {
                        id: userId,
                        isshowOver18,
                    };

                    const addRequest = store.add(newUser);

                    addRequest.onsuccess = () => {
                        resolve();
                    };

                    addRequest.onerror = () => {
                        reject(addRequest.error);
                    };
                }
            };

            getRequest.onerror = () => {
                reject(getRequest.error);
            };
        });
    };

    const genderPreferIndex = 'gender-prefer';

    const setGenderPrefer = async (userId: string, genderPrefer: AIGenderType): Promise<void> => {
        await openDatabase();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction([usersStoreName], 'readwrite');
            const store = transaction.objectStore(usersStoreName);

            // 检查用户是否存在
            const getRequest = store.get(userId);

            getRequest.onsuccess = event => {
                const existingUser = getRequest.result;

                if (existingUser) {
                    // 用户已存在，更新信息
                    const updateRequest = store.put({ ...existingUser, genderPrefer });

                    updateRequest.onsuccess = () => {
                        resolve();
                    };

                    updateRequest.onerror = () => {
                        reject(updateRequest.error);
                    };
                } else {
                    // 用户不存在，添加新记录
                    const newUser = {
                        id: userId,
                        isshowOver18: false,
                        genderPrefer,
                    };

                    const addRequest = store.add(newUser);

                    addRequest.onsuccess = () => {
                        resolve();
                    };

                    addRequest.onerror = () => {
                        reject(addRequest.error);
                    };
                }
            };

            getRequest.onerror = () => {
                reject(getRequest.error);
            };
        });
    };

    const getGenderPrefer = async (userId: string): Promise<AIGenderType | null> => {
        await openDatabase();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([usersStoreName], 'readonly');
            const store = transaction.objectStore(usersStoreName);

            const request = store.get(userId);

            request.onsuccess = event => {
                const { result } = event.target as IDBRequest;
                resolve(result?.genderPrefer || null);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    };

    const getUserForIsOver18 = async (userId: string): Promise<boolean> => {
        await openDatabase();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([usersStoreName], 'readonly');
            const store = transaction.objectStore(usersStoreName);

            const request = store.get(userId);

            request.onsuccess = event => {
                const { result } = event.target as IDBRequest;
                resolve(result?.isshowOver18 || false);
            };

            request.onerror = () => {
                reject(request.error);
            };
        });
    };

    return {
        openDatabase,
        addLike,
        addUnlike,
        isBotLikedByUser,
        getLikesByUser,
        addUser,
        getUser,
        getUserForIsOver18,
        setGenderPrefer,
        getGenderPrefer,
        genderPreferIndex,
    };
};

export default AIChatLocalizeService;
